<app-breadcrumb [titles]="[{ label: 'レビュー一覧' }]"></app-breadcrumb>

<!-- prettier-ignore -->
<div class="content-block">
  @let state = (reviewState$ | async);
  @switch (state?.status) {
    @case ('loading') {
      <app-skeleton type="table" />
      <app-spinner message="読み込み中" />
    }
    @case ('success') {
      @if (isBusy) {
        <app-skeleton type="table" />
        <app-spinner message="読み込み中" />
      }
      @else if (dataSource.data.length === 0) {
        <app-skeleton
          type="table"
          [messages]="['レビュー一覧データが登録されていません']"
        />
      } @else {
        @let patch = (patchState$ | async);
        @let isSaving = patch?.status === 'saving';

        @if (isSaving) { <app-spinner message="登録中" /> }

        <div class="actions-row">
          <button type="button" class="save-button" (click)="saveEdit()" [disabled]="isSaving">
            登録
          </button>
        </div>

        <div class="table-wrapper" #tableContainer>
          <table
            mat-table
            [dataSource]="dataSource"
            [trackBy]="trackById"
            matSort
            [matSortActive]="'created'"
            [matSortDirection]="'desc'"
            matSortDisableClear
            class="mat-table"
          >
            <caption class="table-description">レビュー一覧</caption>
            <!-- id -->
            <ng-container matColumnDef="id">
              <th mat-header-cell *matHeaderCellDef mat-sort-header scope="col">管理番号</th>
              <td mat-cell *matCellDef="let row" class="id">
                <button
                  type="button"
                  class="id-link"
                  [disabled]="isSaving"
                  (click)="$event.stopPropagation(); goToHospitalReviews(row)"
                  (keydown.enter)="$event.stopPropagation(); goToHospitalReviews(row)"
                >
                  {{ row.id | idFormat }}
                </button>
              </td>
            </ng-container>

            <!-- created -->
            <ng-container matColumnDef="created">
              <th mat-header-cell *matHeaderCellDef mat-sort-header scope="col">作成日</th>
              <td mat-cell *matCellDef="let row" class="created">
                {{ row.created | yyyyMMddFormat }}
              </td>
            </ng-container>

            <!-- hospitalName -->
            <ng-container matColumnDef="hospitalName">
              <th mat-header-cell *matHeaderCellDef mat-sort-header scope="col">施設名</th>
              <td 
                mat-cell 
                *matCellDef="let row" 
                class="hospitalName"
                [matTooltip]="row.hospitalName"
                [matTooltipDisabled]="!row.hospitalName"
                matTooltipPosition="above"
                appEllipsis>
                {{ row.hospitalName }}
              </td>
            </ng-container>

            <!-- status（編集可能） -->
            <ng-container matColumnDef="status">
              <th mat-header-cell *matHeaderCellDef mat-sort-header scope="col">レビュー状況</th>
              <td mat-cell *matCellDef="let row" class="status">
                <select
                  #statusSel
                  class="status-select"
                  [class.is-dirty]="initialReviewStatus.get(row.id) !== row.status"
                  [value]="row.status"
                  [disabled]="isSaving"
                  aria-label="レビュー状況を選択"
                  (click)="$event.stopPropagation()"
                  (keydown.enter)="$event.stopPropagation()"
                  (change)="onStatusChange(row.id, statusSel.value)"
                >
                  <option value="preparation">準備中</option>
                  <option value="processing">レビュー中</option>
                  <option value="finished">完了</option>
                </select>
              </td>
            </ng-container>

            <!-- reviewResult -->
            <ng-container matColumnDef="reviewResult">
              <th mat-header-cell *matHeaderCellDef mat-sort-header scope="col">承認状況</th>
              <td mat-cell *matCellDef="let row" class="reviewResult">
                <span
                  class="chip"
                  [ngClass]="{
                    'chip--approved': row.reviewResult === 'approved',
                    'chip--rejected': row.reviewResult === 'rejected',
                    'chip--pending': !row.reviewResult || row.reviewResult === 'null'
                  }"
                >
                  {{ mapReviewResult(row.reviewResult) }}
                </span>
              </td>
            </ng-container>

            <!-- approverName -->
            <ng-container matColumnDef="approverName">
              <th mat-header-cell *matHeaderCellDef mat-sort-header scope="col">承認・却下者</th>
              <td mat-cell *matCellDef="let row" class="approverName">
                {{ row.approverName }}
              </td>
            </ng-container>

            <!-- reviewed -->
            <ng-container matColumnDef="reviewed">
              <th mat-header-cell *matHeaderCellDef mat-sort-header scope="col">承認・却下日</th>
              <td mat-cell *matCellDef="let row" class="reviewed">
                {{ row.reviewed | yyyyMMddFormat }}
              </td>
            </ng-container>

            <!-- comment（tooltip有効） -->
            <ng-container matColumnDef="comment">
              <th mat-header-cell *matHeaderCellDef mat-sort-header scope="col">コメント</th>
              <td
                mat-cell
                *matCellDef="let row"
                class="comment"
                [matTooltip]="row.comment"
                [matTooltipDisabled]="!row.comment"
                matTooltipPosition="above"
                appEllipsis
              >
                {{ row.comment }}
              </td>
            </ng-container>

            <!-- updated -->
            <ng-container matColumnDef="updated">
              <th mat-header-cell *matHeaderCellDef mat-sort-header scope="col">レコード更新日時</th>
              <td mat-cell *matCellDef="let row" class="updated">
                {{ row.updated | yyyyMMddFormat:'datetime' }}
              </td>
            </ng-container>

            <!-- updaterName-->
            <ng-container matColumnDef="updaterName">
              <th mat-header-cell *matHeaderCellDef mat-sort-header scope="col">レコード更新者</th>
              <td mat-cell *matCellDef="let row" class="updaterName">
                {{ row.updaterName }}
              </td>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="displayedColumns" sticky></tr>

            <tr
              mat-row
              *matRowDef="let row; columns: displayedColumns"
              (click)="onSelect(row)"
              (keydown.enter)="onSelect(row)"
              [class.selected-row]="selectedItemId === row.id"
              [class.initial-finished]="initialReviewStatus.get(row.id) === 'finished'"
              tabindex="0"
              [attr.aria-selected]="selectedItemId === row.id"
              [attr.data-row-id]="row.id"
            >
            </tr>

            <tr class="mat-row" *matNoDataRow>
              <td class="mat-cell" [attr.colspan]="displayedColumns.length">
                条件に一致するデータがありません。
              </td>
            </tr>
          </table>
        </div>
      }
    }
    @case ('error') {
      <app-skeleton
        type="alert"
        [messages]="['レビュー一覧データの取得に失敗しました。','しばらくしてから再度お試しください。']"
      />
    }
  }
</div>
